import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import Pagination from '../components/UI/Pagination';
import { backendUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

axios.defaults.withCredentials = true;

const C = {
    bg:     '#060b1f',
    card:   'rgba(13,19,50,0.65)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    green:  '#00c853',
    amber:  '#ffb300',
    red:    '#ff3b30',
};

const fmtDate = d => {
    try {
        const x = new Date(d);
        return `${x.toLocaleDateString()} ${x.toLocaleTimeString()}`;
    } catch { return '–'; }
};
const fmtPrice = pricing => `${Number(pricing?.totalPrice ?? 0)} ${(pricing?.currency || 'credits') === 'credits' ? 'cr' : pricing?.currency}`;

const StatusBadge = ({ completed }) => {
    const s = completed
        ? { bg: 'rgba(0,200,83,0.12)', border: 'rgba(0,200,83,0.3)', color: '#4ade80', label: 'Completed' }
        : { bg: 'rgba(0,212,255,0.12)', border: 'rgba(0,212,255,0.3)', color: '#00d4ff', label: 'Playing' };
    return (
        <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
            {s.label}
        </span>
    );
};

const StatCard = ({ icon, label, value, color }) => (
    <div style={{
        background: C.card, backdropFilter: 'blur(14px)',
        border: `1px solid ${C.border}`, borderRadius: 16,
        padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 16,
    }}>
        <div style={{ fontSize: 32 }}>{icon}</div>
        <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: color || '#fff' }}>{value}</div>
        </div>
    </div>
);

export default function Dashboard() {
    const { user, isLoggedIn, isAdmin, loading: authLoading } = useAuth();

    const [games,      setGames]      = useState([]);
    const [loading,    setLoading]    = useState(true);   // first load only — later fetches keep the table mounted
    const [refreshing, setRefreshing] = useState(false);
    const [error,      setError]      = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
    const [stats,      setStats]      = useState({ total: 0, completed: 0, active: 0 });

    // Which endpoint to use: admin sees all, logged-in user sees own
    const historyEndpoint = isAdmin
        ? `${backendUrl}/api/history/all`
        : `${backendUrl}/api/history/my`;

    const fetchGames = useCallback(async (page = 1, limit = 10) => {
        if (!isLoggedIn) return;
        setRefreshing(true); setError(null);
        try {
            const res = await axios.get(historyEndpoint, { params: { page, limit } });
            const data = res.data.data ?? [];
            const pag  = res.data.pagination ?? { total: data.length, page, limit, pages: 1 };
            setGames(data);
            setPagination({ page: pag.page, pages: pag.pages, total: pag.total, limit: pag.limit });
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load game history');
        } finally { setRefreshing(false); setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isAdmin]);

    const fetchStats = useCallback(async () => {
        try {
            if (isLoggedIn) {
                const [allRes, completedRes] = await Promise.all([
                    axios.get(historyEndpoint, { params: { page: 1, limit: 1 } }),
                    axios.get(historyEndpoint, { params: { page: 1, limit: 1, completed: 'true' } }),
                ]);
                const total     = allRes.data.pagination?.total ?? 0;
                const completed = completedRes.data.pagination?.total ?? 0;
                setStats({ total, completed, active: Math.max(0, total - completed) });
            } else {
                // Guest: fetch global stats
                const res = await axios.get(`${backendUrl}/api/history/stats`);
                setStats({ total: res.data.total ?? 0, completed: res.data.completed ?? 0, active: res.data.active ?? 0 });
            }
        } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, isAdmin]);

    useEffect(() => {
        if (authLoading) return;
        fetchStats();
        if (isLoggedIn) fetchGames(1, 10);
        else setLoading(false);
    }, [authLoading, isLoggedIn, fetchGames, fetchStats]);

    if (authLoading) return null;
    if (!isLoggedIn) return <Navigate to="/auth/login" replace />;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-24 pb-24 md:pb-8 px-4 md:pl-20"
            style={{ maxWidth: 1100, marginLeft: 'auto', marginRight: 'auto' }}
        >
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
                <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>
                    🎮 Dashboard
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '5px 0 0' }}>
                    {isLoggedIn
                        ? isAdmin
                            ? `Admin view — all games globally`
                            : `Welcome back, ${user?.username || 'Player'} — your games`
                        : 'Your game dashboard'}
                </p>
            </div>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
                <StatCard icon="🎲" label={isAdmin ? 'Total Games' : isLoggedIn ? 'My Games' : 'Total Games'} value={stats.total}     color={C.cyan}  />
                <StatCard icon="✅" label="Completed"       value={stats.completed} color={C.green} />
                <StatCard icon="🔥" label="Active Now"      value={stats.active}    color={C.amber} />
            </div>

            {/* Games table */}
            {(
                <div style={{ background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`, borderRadius: 18, overflow: 'hidden' }}>
                    <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                        <div>
                            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                                {isAdmin ? 'All Games' : 'My Game History'}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 2 }}>
                                {pagination.total} games total
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                onClick={() => fetchGames(pagination.page, pagination.limit)}
                                disabled={refreshing}
                                style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1 }}
                            >{refreshing ? '… Refreshing' : '↻ Refresh'}</button>
                            <Link to="/" style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, background: `rgba(0,212,255,0.1)`, border: `1px solid rgba(0,212,255,0.3)`, color: C.cyan, textDecoration: 'none', fontWeight: 600 }}>
                                + New Game
                            </Link>
                        </div>
                    </div>

                    {error && (
                        <div style={{ padding: '14px 22px', background: 'rgba(255,59,48,0.08)', borderBottom: `1px solid rgba(255,59,48,0.2)`, color: C.red, fontSize: 13 }}>
                            ⚠ {error}
                        </div>
                    )}

                    {loading ? (
                        <div style={{ padding: '80px 0', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                            <div style={{ width: 36, height: 36, border: '2px solid rgba(0,212,255,0.2)', borderTop: `2px solid ${C.cyan}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                            Loading game history…
                        </div>
                    ) : games.length === 0 ? (
                        <div style={{ padding: '80px 0', textAlign: 'center' }}>
                            <div style={{ fontSize: 40, opacity: 0.25, marginBottom: 14 }}>🎲</div>
                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>No games found</div>
                            <Link to="/" style={{ display: 'inline-block', marginTop: 14, padding: '9px 20px', borderRadius: 10, background: `rgba(0,212,255,0.12)`, border: `1px solid rgba(0,212,255,0.3)`, color: C.cyan, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>
                                Start Playing
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div style={{ overflowX: 'auto', opacity: refreshing ? 0.55 : 1, transition: 'opacity 0.2s' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                            {['Room', 'Players', 'Password', 'Price', 'Status', 'Winner', 'Date'].map((h, i) => (
                                                <th key={h} style={{
                                                    padding: '12px 16px', textAlign: 'left',
                                                    fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)',
                                                    textTransform: 'uppercase', letterSpacing: 1,
                                                    background: 'rgba(0,0,0,0.1)', whiteSpace: 'nowrap',
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {games.map((g, idx) => (
                                            <tr
                                                key={g._id || g.roomId}
                                                style={{
                                                    borderBottom: `1px solid rgba(255,255,255,0.04)`,
                                                    background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                                                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
                                            >
                                                <td style={{ padding: '11px 16px' }}>
                                                    {g.roomCode && (
                                                        <div style={{ fontFamily: 'monospace', fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 2 }}>
                                                            {g.roomCode}
                                                        </div>
                                                    )}
                                                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', fontFamily: 'monospace' }}>{g.roomId}</div>
                                                </td>
                                                <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                                                    {g.maxPlayers ? `${g.maxPlayers}P` : '—'}
                                                    {Array.isArray(g.players) && g.players.length > 0 && (
                                                        <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                                                            {g.players.map(p => p.name || p).filter(Boolean).join(', ')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '11px 16px' }}>
                                                    {g.password
                                                        ? <span style={{ fontSize: 11, color: 'rgba(255,179,0,0.8)', fontWeight: 600 }}>🔒 Password set</span>
                                                        : <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>None</span>
                                                }
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
                                                {fmtPrice(g.pricing)}
                                            </td>
                                            <td style={{ padding: '11px 16px' }}>
                                                    <StatusBadge completed={g.completed} />
                                                </td>
                                                <td style={{ padding: '11px 16px', fontSize: 11 }}>
                                                    {g.winner
                                                        ? <span style={{ color: C.amber, fontWeight: 700 }}>🏆 {g.winner}</span>
                                                        : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
                                                </td>
                                                <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                                                    {fmtDate(g.createdAt || g.date)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                                <Pagination
                                    page={pagination.page}
                                    pages={pagination.pages}
                                    total={pagination.total}
                                    limit={pagination.limit}
                                    onPage={p => fetchGames(p, pagination.limit)}
                                    onLimit={l => { setPagination(prev => ({ ...prev, limit: l })); fetchGames(1, l); }}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </motion.div>
    );
}
